// Constants
export const REROUTE_THRESHOLD = 50; // meters - distance from route to trigger reroute

// Utility functions
export const toRadians = (deg) => deg * Math.PI / 180;
export const toDegrees = (rad) => rad * 180 / Math.PI;

/**
 * Calculate distance between two geographic points using the Haversine formula
 * 
 * The Haversine formula determines the great-circle distance between two points
 * on a sphere given their longitudes and latitudes. This is useful for navigation
 * and is accurate for most purposes (assumes Earth is a perfect sphere).
 * 
 * Formula: a = sin²(Δφ/2) + cos(φ1)⋅cos(φ2)⋅sin²(Δλ/2)
 *          c = 2⋅atan2(√a, √(1−a))
 *          d = R⋅c
 * 
 * Where:
 *   φ = latitude, λ = longitude, R = Earth's radius
 *   Δφ = φ2−φ1, Δλ = λ2−λ1
 * 
 * @param {number} lat1 - First point latitude in degrees
 * @param {number} lon1 - First point longitude in degrees
 * @param {number} lat2 - Second point latitude in degrees
 * @param {number} lon2 - Second point longitude in degrees
 * @returns {number} Distance in meters
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = toRadians(lat1);
    const φ2 = toRadians(lat2);
    const Δφ = toRadians(lat2 - lat1);
    const Δλ = toRadians(lon2 - lon1);

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) *
        Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
};

/**
 * Calculate bearing (direction) from one point to another using spherical trigonometry
 * 
 * Bearing represents the compass direction from the start point to the destination point,
 * measured in degrees clockwise from north (0° = North, 90° = East, 180° = South, 270° = West).
 * 
 * @param {number} startLat - Starting point latitude in degrees
 * @param {number} startLng - Starting point longitude in degrees
 * @param {number} destLat - Destination point latitude in degrees
 * @param {number} destLng - Destination point longitude in degrees
 * @returns {number} Bearing in degrees (0-360)
 */
export const calculateBearing = (startLat, startLng, destLat, destLng) => {
    const startLatRad = toRadians(startLat);
    const startLngRad = toRadians(startLng);
    const destLatRad = toRadians(destLat);
    const destLngRad = toRadians(destLng);

    const y = Math.sin(destLngRad - startLngRad) * Math.cos(destLatRad);
    const x = Math.cos(startLatRad) * Math.sin(destLatRad) -
        Math.sin(startLatRad) * Math.cos(destLatRad) * Math.cos(destLngRad - startLngRad);
    let brng = Math.atan2(y, x);
    brng = toDegrees(brng);
    return (brng + 360) % 360;
};

export const findActiveTrafficLight = (currentLocation, intersections, currentBearing, routeProximity, approachAngle = 45) => {
    if (!intersections || !intersections.features || !currentLocation) {
        return { activeLight: null, distance: Infinity };
    }

    let validCandidates = [];

    intersections.features.forEach(feature => {
        const { geometry, properties } = feature;
        if (!geometry || !geometry.coordinates) return;

        // coordinates are [lng, lat]
        const lat = geometry.coordinates[0][1];
        const lng = geometry.coordinates[0][0];

        const dist = calculateDistance(
            currentLocation.lat,
            currentLocation.lng,
            lat,
            lng
        );

        if (dist <= routeProximity) {
            const bearingToLight = calculateBearing(currentLocation.lat, currentLocation.lng, lat, lng);
            const bearingDiff = Math.abs(currentBearing - bearingToLight);
            // Normalize diff to 0-180
            const normalizedDiff = bearingDiff > 180 ? 360 - bearingDiff : bearingDiff;

            // Check if within approach angle
            if (normalizedDiff <= approachAngle) {
                validCandidates.push({
                    ...properties,
                    lat,
                    lng,
                    distance: dist
                });
            }
        }
    });

    // Sort by distance and pick the closest
    if (validCandidates.length > 0) {
        validCandidates.sort((a, b) => a.distance - b.distance);
        return { activeLight: validCandidates[0], distance: validCandidates[0].distance };
    } else {
        return { activeLight: null, distance: Infinity };
    }
};

/**
 * Find the nearest point on the route to the current location
 * @param {Object} currentLocation - { lat, lng }
 * @param {Array} routeCoordinates - Array of { lat, lng } points
 * @returns {Object} - { nearestPoint: { lat, lng }, distance: number, index: number }
 */
export const findNearestPointOnRoute = (currentLocation, routeCoordinates) => {
    if (!currentLocation || !routeCoordinates || routeCoordinates.length === 0) {
        return { nearestPoint: null, distance: Infinity, index: -1 };
    }

    let minDistance = Infinity;
    let nearestPoint = null;
    let nearestIndex = -1;

    routeCoordinates.forEach((point, index) => {
        const dist = calculateDistance(
            currentLocation.lat,
            currentLocation.lng,
            point.lat,
            point.lng
        );

        if (dist < minDistance) {
            minDistance = dist;
            nearestPoint = point;
            nearestIndex = index;
        }
    });

    return { nearestPoint, distance: minDistance, index: nearestIndex };
};

/**
 * Check if the current location is off the route
 * @param {Object} currentLocation - { lat, lng }
 * @param {Array} routeCoordinates - Array of { lat, lng } points
 * @param {Number} threshold - Distance threshold in meters (default: REROUTE_THRESHOLD)
 * @returns {Boolean} - true if off route, false otherwise
 */
export const checkIfOffRoute = (currentLocation, routeCoordinates, threshold = REROUTE_THRESHOLD) => {
    const { distance } = findNearestPointOnRoute(currentLocation, routeCoordinates);
    return distance > threshold;
};
